import { type ConversationDetail, CSRF_HEADER, type MovePreview } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Paperclip, ShieldAlert, Undo2, UserMinus } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { formatList } from "../i18n/format.ts";
import { modelOptionKeys } from "../models/queries.ts";
import { useProjects } from "../projects/queries.ts";
import { navigate, projectConversationPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { conversationKeys } from "./queries.ts";

/** Set by a move: the moved conversation's header takes focus once it opens at its new URL. */
let focusHeaderOf: string | null = null;

/** Whether the header of `id` should take focus (after a move), without consuming it. */
export function headerFocusPending(id: string): boolean {
  return focusHeaderOf === id;
}

/** Whether the header of `id` should take focus now (once, after a move). */
export function takeHeaderFocus(id: string): boolean {
  if (focusHeaderOf === id) {
    focusHeaderOf = null;
    return true;
  }
  return false;
}

/**
 * "Move to project" (spec `web-shell` "Move dialog"): pick a project the user may start
 * conversations in, read the move's effects from the API (lapsing shares, emergency access,
 * sources-only mode, one way), then confirm. Refusals stay in the dialog in plain words; a
 * change since the preview reloads it.
 */
export function MoveDialog(props: {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  returnFocus?: HTMLElement | null;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("move.title")}
      description={t("move.explain")}
      returnFocus={props.returnFocus ?? null}
    >
      <MoveForm conversationId={props.conversationId} onClose={props.onClose} />
    </AppDialog>
  );
}

function MoveForm(props: { conversationId: string; onClose: () => void }) {
  const { conversationId, onClose } = props;
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const projects = useProjects();
  // Any role may converse in a project; a frozen one takes no conversation.
  const targets = (projects.data ?? []).filter((p) => p.frozenAt === null);
  const [picked, setPicked] = useState<string | null>(null);
  const target = targets.find((p) => p.id === picked)?.id ?? null;
  const [changed, setChanged] = useState(false);
  const preview = useQuery({
    queryKey: ["conversation-move", conversationId, target],
    enabled: target !== null,
    gcTime: 0,
    retry: false,
    queryFn: async (): Promise<MovePreview> =>
      ok(
        await api.api.conversations[":conversationId"].move.$get({
          param: { conversationId },
          query: { projectId: target ?? "" },
        }),
      ),
  });
  const move = useMutation({
    mutationFn: async (input: { projectId: string; fingerprint: string }) =>
      ok(
        await api.api.conversations[":conversationId"].move.$post(
          { param: { conversationId }, json: input },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: (moved) => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists });
      // Known now: the project URL opens at once, never bounced back by the old answer.
      queryClient.setQueryData<ConversationDetail>(
        conversationKeys.detail(conversationId),
        (old) =>
          old && effects ? { ...old, projectId: moved.projectId, project: effects.project } : old,
      );
      void queryClient.invalidateQueries({ queryKey: conversationKeys.detail(conversationId) });
      // Its picker now follows the project's policy.
      void queryClient.invalidateQueries({
        queryKey: modelOptionKeys.of({ conversationId }),
        exact: true,
      });
      focusHeaderOf = conversationId;
      onClose();
      navigate(projectConversationPath(moved.projectId ?? "", conversationId));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "move_changed") {
        setChanged(true);
        void preview.refetch();
      }
    },
  });
  const refusal = (error: Error | null) => {
    if (!(error instanceof ApiError)) {
      return error?.message ?? null;
    }
    switch (error.code) {
      case "move_changed":
        return null;
      case "residency_narrower":
        return t("move.residency");
      case "not_standalone":
        return t("move.notStandalone");
      case "turn_in_progress":
        return t("move.turnInProgress");
      case "project_frozen":
        return t("move.frozen");
      default:
        return error.message;
    }
  };
  const effects = preview.data;
  const blocked = effects ? !effects.residencyOk : true;
  const message = refusal(move.error) ?? (preview.isError ? refusal(preview.error) : null);

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (effects && target && !blocked && !move.isPending) {
          setChanged(false);
          move.mutate({ projectId: target, fingerprint: effects.fingerprint });
        }
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-project`}>{t("move.project")}</Label>
        {projects.isPending ? (
          <Skeleton className="h-9 w-full" />
        ) : targets.length === 0 ? (
          <p className="text-sm text-fg-secondary">{t("move.noProject")}</p>
        ) : (
          <NativeSelect
            id={`${id}-project`}
            value={target ?? ""}
            onChange={(e) => {
              setPicked(e.currentTarget.value || null);
              setChanged(false);
              move.reset();
            }}
          >
            <option value="">{t("move.pick")}</option>
            {targets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        )}
      </div>

      {target === null ? null : preview.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : effects ? (
        <section aria-label={t("move.effects")} aria-live="polite" className="flex flex-col gap-3">
          {changed ? <Notice tone="warning">{t("move.changed")}</Notice> : null}
          {effects.residencyOk ? null : <Notice tone="danger">{t("move.residency")}</Notice>}
          <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised text-sm">
            <Effect icon={ArrowRight}>{t("move.into", { project: effects.project.name })}</Effect>
            <Effect icon={UserMinus}>
              {effects.lapsingShares.length === 0
                ? t("move.noLapsing")
                : t("move.lapsing", {
                    count: effects.lapsingShares.length,
                    names: formatList(
                      effects.lapsingShares.map((s) => s.name ?? t("share.someone")),
                    ),
                  })}
            </Effect>
            {effects.attachmentCount ? (
              <Effect icon={Paperclip}>
                <span className="flex flex-col gap-0.5">
                  <span>{t("move.attachments", { count: effects.attachmentCount })}</span>
                  {/* Absent from an older server: no claim about privacy then. */}
                  {effects.privateAttachmentCount ? (
                    <span>
                      {effects.privateAttachmentCount === effects.attachmentCount
                        ? t("move.attachmentsAllPrivate")
                        : t("move.attachmentsPrivate", {
                            count: effects.privateAttachmentCount,
                          })}
                    </span>
                  ) : null}
                </span>
              </Effect>
            ) : null}
            <Effect icon={ShieldAlert}>{t("move.breakGlass")}</Effect>
            {effects.sourcesOnly ? (
              <Effect icon={ShieldAlert}>{t("move.sourcesOnly")}</Effect>
            ) : null}
            <Effect icon={Undo2}>{t("move.oneWay")}</Effect>
          </ul>
        </section>
      ) : null}

      {message ? <Notice tone="danger">{message}</Notice> : null}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t("app.cancel")}
        </Button>
        <Button type="submit" disabled={!effects || blocked || move.isPending}>
          {move.isPending
            ? t("move.moving")
            : effects
              ? t("move.confirm", { project: effects.project.name })
              : t("move.confirmShort")}
        </Button>
      </DialogFooter>
    </form>
  );
}

function Effect(props: { icon: typeof ArrowRight; children: React.ReactNode }) {
  const Icon = props.icon;
  return (
    <li className="flex items-start gap-2.5 px-3 py-2.5 text-fg-secondary">
      <Icon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-fg-muted"
        strokeWidth={1.75}
      />
      <span>{props.children}</span>
    </li>
  );
}
